import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Cookie, X, ChevronRight } from "lucide-react";
import { API } from "@/lib/api";

const STORAGE_KEY = "glms_cookie_consent";

const defaultConsent = {
    essential: true,
    analytics: false,
    marketing: false,
    version: 1,
};

/**
 * Bottom-sheet cookie banner. Shown at first visit; dismissed to
 * localStorage. Emits POST /api/cookie-consent for the audit trail
 * required by GDPR (see /legal/gdpr).
 */
export default function CookieBanner() {
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);
    const [customizeOpen, setCustomizeOpen] = useState(false);
    const [prefs, setPrefs] = useState(defaultConsent);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (!stored) setVisible(true);
    }, []);

    const persistLocally = (next) => {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    };

    const submit = async (categories, source) => {
        try {
            await fetch(`${API}/cookie-consent`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ ...categories, source }),
            });
        } catch (e) {
            // Silent: consent is stored client-side anyway.
        }
    };

    const handleAcceptAll = async () => {
        const next = { essential: true, analytics: true, marketing: true, version: 1 };
        persistLocally(next);
        setVisible(false);
        await submit(next, "banner_accept_all");
    };

    const handleReject = async () => {
        const next = { essential: true, analytics: false, marketing: false, version: 1 };
        persistLocally(next);
        setVisible(false);
        await submit(next, "banner_reject_nonessential");
    };

    const handleSaveCustom = async () => {
        const next = { ...prefs, essential: true, version: 1 };
        persistLocally(next);
        setVisible(false);
        setCustomizeOpen(false);
        await submit(next, "banner_customize");
    };

    if (!visible) return null;

    return (
        <>
            <div
                data-testid="cookie-banner"
                className="fixed bottom-0 left-0 right-0 z-[var(--z-banner)] border-t border-[var(--color-border)] bg-[var(--color-card)]/95 backdrop-blur-xl px-6 py-4 shadow-2xl animate-fade-in-up"
                role="dialog"
                aria-label="Cookie consent"
            >
                <div className="mx-auto max-w-5xl flex flex-col md:flex-row gap-4 items-start md:items-center">
                    <div className="flex gap-3 items-start flex-1">
                        <Cookie className="h-5 w-5 text-[var(--color-secondary)] mt-0.5 shrink-0" />
                        <div>
                            <h3 className="text-sm font-medium text-text-main mb-1">
                                {t("cookie.title")}
                            </h3>
                            <p className="text-xs text-text-secondary leading-relaxed max-w-2xl">
                                {t("cookie.body")}{" "}
                                <a
                                    href="/legal/cookies"
                                    className="underline hover:text-text-main"
                                    data-testid="cookie-banner-policy-link"
                                >
                                    {t("cookie.learn_more")}
                                </a>
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-2 items-center">
                        <button
                            data-testid="cookie-customize"
                            onClick={() => setCustomizeOpen(true)}
                            className="text-xs text-text-secondary hover:text-text-main px-3 py-2 transition-colors"
                        >
                            {t("cookie.customize")}
                        </button>
                        <button
                            data-testid="cookie-reject"
                            onClick={handleReject}
                            className="text-xs rounded-full border border-[var(--color-border)] px-4 py-2 hover:border-[var(--color-primary)]/50 transition-colors"
                        >
                            {t("cookie.reject_non_essential")}
                        </button>
                        <button
                            data-testid="cookie-accept-all"
                            onClick={handleAcceptAll}
                            className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-2 font-medium hover:opacity-90 transition-opacity"
                        >
                            {t("cookie.accept_all")}
                        </button>
                    </div>
                </div>
            </div>

            {customizeOpen && (
                <div
                    className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
                    onClick={() => setCustomizeOpen(false)}
                >
                    <div
                        data-testid="cookie-customize-modal"
                        className="w-full max-w-lg rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-6 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start justify-between mb-4">
                            <h2 className="text-lg font-semibold">{t("cookie.customize_title")}</h2>
                            <button
                                onClick={() => setCustomizeOpen(false)}
                                data-testid="cookie-customize-close"
                                className="text-text-secondary hover:text-text-main"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>

                        <div className="space-y-3">
                            <CategoryRow
                                testId="cookie-cat-essential"
                                title={t("cookie.cat_essential")}
                                description={t("cookie.cat_essential_desc")}
                                checked={true}
                                disabled={true}
                            />
                            <CategoryRow
                                testId="cookie-cat-analytics"
                                title={t("cookie.cat_analytics")}
                                description={t("cookie.cat_analytics_desc")}
                                checked={prefs.analytics}
                                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                            />
                            <CategoryRow
                                testId="cookie-cat-marketing"
                                title={t("cookie.cat_marketing")}
                                description={t("cookie.cat_marketing_desc")}
                                checked={prefs.marketing}
                                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                            />
                        </div>

                        <div className="flex justify-end gap-2 mt-6">
                            <button
                                onClick={() => setCustomizeOpen(false)}
                                className="text-xs px-4 py-2 text-text-secondary hover:text-text-main"
                            >
                                {t("common.cancel")}
                            </button>
                            <button
                                data-testid="cookie-customize-save"
                                onClick={handleSaveCustom}
                                className="text-xs rounded-full bg-white hover:bg-[#EDEDED] text-black px-4 py-2 font-medium hover:opacity-90"
                            >
                                {t("cookie.save_prefs")}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function CategoryRow({ testId, title, description, checked, disabled = false, onChange }) {
    return (
        <div
            data-testid={testId}
            className="rounded-xl border border-[var(--color-border)] bg-[var(--color-panel-ai)] px-4 py-3 flex items-start justify-between gap-4"
        >
            <div className="flex-1">
                <div className="text-sm font-medium text-text-main">{title}</div>
                <p className="text-xs text-text-secondary mt-0.5 leading-relaxed">{description}</p>
            </div>
            <label className={`relative inline-flex items-center ${disabled ? "opacity-60" : "cursor-pointer"}`}>
                <input
                    type="checkbox"
                    checked={checked}
                    disabled={disabled}
                    onChange={(e) => onChange && onChange(e.target.checked)}
                    className="sr-only peer"
                />
                <div className="w-9 h-5 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-full peer-checked:bg-[var(--color-primary)]/30 peer-checked:border-[var(--color-primary)] transition-colors after:content-[''] after:absolute after:left-[3px] after:top-[3px] after:bg-text-secondary peer-checked:after:bg-[var(--color-secondary)] after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:after:translate-x-4" />
            </label>
        </div>
    );
}
